/**
 * RELEASE HISTORY — Help › Releases (design_handoff_track_polish §14).
 *
 * Reads public/changelog.js and NOTHING ELSE. Every release Track has ever had
 * is already written there, in the words it was written in at the time; this
 * screen does not re-author a line of it. Both eras are in that one file —
 * Datum from 13 July, Benchmark from 7 August — and both are shown, because
 * pretending the first month did not happen would be the only editorial
 * decision on the screen.
 *
 * TWO THINGS THE FILE GETS WRONG AND THIS CORRECTS:
 *   · it has two `Benchmark v3.7.0` entries, written a day apart. A version is
 *     one release, so they are merged before anything is counted — otherwise
 *     the headline figure is quietly one too many, forever;
 *   · a role-gated item ({ text, roles }) must not reach someone outside those
 *     roles. The splash already filters exactly this way; so does this, and a
 *     release whose every item is gated away is not shown at all.
 */
import { escapeHtml } from '../ui.js';
import { CHANGELOG } from '../changelog.js';
import { fillKeys } from '../keys.js';
import { orgInfo } from '../org-info.js';

const RELEASE_LINES = ['Datum', 'Benchmark', 'Track'];   // Track v4.0.0 (23/10/2026)
const PAGE = 25;

function versionNumber(v) {
  const s = String(v || '');
  const name = (s.match(/^([A-Za-z]+)/) || [])[1] || '';
  const gen = RELEASE_LINES.indexOf(name) + 1;
  const m = s.match(/(\d+)\.(\d+)(?:\.(\d+))?/);
  const num = m ? (Number(m[1]) * 1e6 + Number(m[2]) * 1e3 + Number(m[3] || 0)) : 0;
  return gen * 1e9 + num;
}
/** "23 Sep 2026" and "13 Jul 2026 · 18:00" both parse; the time is ignored. */
const dateOf = (d) => {
  const s = String(d || '').split('·')[0].trim();
  const t = Date.parse(s);
  return isNaN(t) ? null : new Date(t);
};
const isFix = (text) => /^(fixed|fix|housekeeping)\b/i.test(String(text || '').trim());
const itemText = (it) => (typeof it === 'string' ? it : (it && it.text) || '');
const itemRoles = (it) => (typeof it === 'string' ? null : (it && it.roles) || null);

/** One release per version, items merged, newest first. */
export function mergeReleases(log) {
  const byVersion = new Map();
  for (const e of log) {
    const v = String(e.v || '');
    if (!byVersion.has(v)) byVersion.set(v, { v, date: e.date, items: [] });
    const row = byVersion.get(v);
    row.items.push(...(e.items || []));
    // The earlier of the two dates is when the version actually shipped.
    if (dateOf(e.date) && dateOf(row.date) && dateOf(e.date) < dateOf(row.date)) row.date = e.date;
  }
  return [...byVersion.values()].sort((a, b) => versionNumber(b.v) - versionNumber(a.v));
}

/** Drop what this viewer may not read; drop the release if that empties it. */
export function forRoles(releases, roles, email = '', template = '') {
  const mine = (it) => {
    // A contractor note never reaches ASP, and an ASP note never reaches a contractor (v4.2.0).
    const only = typeof it === 'string' ? null : (it && it.templates) || null;
    if (only && !only.includes(template || 'consultancy')) return false;
    const r = itemRoles(it);
    if (r && !r.some((x) => roles.includes(x))) return false;
    const mail = typeof it === 'string' ? null : (it && it.emails) || null;
    return !mail || mail.includes(String(email || '').toLowerCase());
  };
  return releases
    .map((r) => ({ ...r, items: r.items.filter(mine), hidden: r.items.length - r.items.filter(mine).length }))
    .filter((r) => r.items.length);
}

/** "22 Oct", with the year only when it is not this year (p39). */
const shortDate = (d) => {
  const x = dateOf(d);
  if (!x) return String(d || '');
  const y = x.getFullYear() !== new Date().getFullYear() ? ` ${x.getFullYear()}` : '';
  return `${x.getDate()} ${x.toLocaleDateString('en-GB', { month: 'short' })}${y}`;
};
/** "Benchmark v3.60.17" → "3.60.17" — the era is history, not the version. */
const bareVersion = (v) => (String(v || '').match(/\d+\.\d+(?:\.\d+)?/) || [String(v || '')])[0];
/** The words without the leading "Fixed: " (the badge says it), first letter up. */
const changeText = (t) => {
  const s = String(t || '').trim().replace(/^(fixed|fix|housekeeping)\s*:\s*/i, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * RELEASES — v4.0.0 refresh (p39): a flat table, one row per change, newest
 * first. Version · date · type · what changed. The version is written once,
 * on its first change; the rest of that release's changes sit under it.
 */
export function renderReleases(mount, session, { onGo, search: startSearch = '' } = {}) {
  const roles = (session && session.roles) || [];
  const all = forRoles(mergeReleases(CHANGELOG), roles, session && session.email, orgInfo().template || '');
  const first = all.length ? dateOf(all[all.length - 1].date) : null;
  const since = first ? ` since ${first.getDate()} ${first.toLocaleDateString('en-GB', { month: 'long' })}${first.getFullYear() !== new Date().getFullYear() ? ` ${first.getFullYear()}` : ''}` : '';

  let filter = 'all';
  let q = String(startSearch || '').trim().toLowerCase();
  let shown = PAGE;
  const FILTERS = [['all', 'All'], ['new', 'New'], ['fix', 'Fixes'], ['role', 'For my role']];
  const rowsOf = () => {
    const out = [];
    for (const r of all) {
      const its = r.items.filter((it) => {
        const t = itemText(it);
        if (filter === 'fix' && !isFix(t)) return false;
        if (filter === 'new' && isFix(t)) return false;
        if (filter === 'role' && !itemRoles(it)) return false;
        if (!q) return true;
        const hay = `${r.v} ${bareVersion(r.v)} ${r.date} ${t}`.toLowerCase();
        return q.split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
      });
      its.forEach((it, k) => out.push({ r, it, first: k === 0 }));
    }
    return out;
  };

  mount.innerHTML = `
    <div class="rl4">
      <nav class="crumbs" aria-label="Breadcrumb"><button type="button" data-crumb>Help</button><span>/</span><span>Releases</span></nav>
      <div class="page-h"><h1>Releases</h1><span class="page-sub">${escapeHtml(String(all.length))}${escapeHtml(since)} · every one in the words it shipped with</span></div>
      <div class="rl4-controls">
        <div class="seg" role="tablist" aria-label="Show">${FILTERS.map(([id, label]) => `<button type="button" role="tab" data-f="${id}" class="${id === filter ? 'active' : ''}" aria-selected="${id === filter}">${label}</button>`).join('')}</div>
        <label class="rl4-search">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="5.4" stroke="currentColor" stroke-width="1.6"></circle><path d="M13.2 13.2 L17 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path></svg>
          <input id="rl-search" type="search" autocomplete="off" placeholder="Search a version or a change" aria-label="Search a version or a change" value="${escapeHtml(startSearch || '')}">
        </label>
      </div>
      <div id="rl-list"></div>
    </div>`;

  const list = mount.querySelector('#rl-list');
  const draw = () => {
    const rows = rowsOf();
    const page = rows.slice(0, shown);
    list.innerHTML = rows.length ? `
      <table class="data rl4-table">
        <thead><tr><th>Version</th><th>Date</th><th>Type</th><th>What changed</th></tr></thead>
        <tbody class="arrive">${page.map(({ r, it, first }) => {
          const t = itemText(it);
          const rs = itemRoles(it);
          return `<tr class="${first ? 'first' : 'more'}">
            <td class="rl4-v">${first ? escapeHtml(bareVersion(r.v)) : ''}</td>
            <td class="rl4-d">${first ? escapeHtml(shortDate(r.date)) : ''}</td>
            <td><span class="rl4-type ${isFix(t) ? 'fix' : 'new'}">${isFix(t) ? 'Fix' : 'New'}</span></td>
            <td class="rl4-text">${fillKeys(escapeHtml(changeText(t)), { touch: typeof it === 'string' ? null : it.touch })}${rs ? ` <span class="rl4-tag">${escapeHtml(rs.join(' · '))} only</span>` : ''}</td>
          </tr>`;
        }).join('')}</tbody>
      </table>
      ${rows.length > shown ? `<button type="button" class="btn rl4-more" id="rl-more">Show ${Math.min(PAGE, rows.length - shown)} more</button>` : ''}`
      : '<div class="empty-state"><div class="empty-title">No release matches that</div><p>Try a version number, like 3.60, or a word from the change.</p></div>';
    list.querySelector('#rl-more')?.addEventListener('click', () => { shown += PAGE; draw(); });
  };

  mount.querySelector('[data-crumb]').addEventListener('click', () => onGo && onGo('help'));
  mount.querySelectorAll('[data-f]').forEach((b) => b.addEventListener('click', () => {
    filter = b.dataset.f; shown = PAGE;
    mount.querySelectorAll('[data-f]').forEach((x) => { x.classList.toggle('active', x === b); x.setAttribute('aria-selected', String(x === b)); });
    draw();
  }));
  const search = mount.querySelector('#rl-search');
  search.addEventListener('input', () => { q = search.value.trim().toLowerCase(); shown = PAGE; draw(); });
  draw();
}
